import { useCallback, useEffect, useMemo, useState } from 'react';
import locale from '@glu/locale';
import { ColumnEvent, IHeaderParams } from 'ag-grid-community';
import { LinesAshIcon, FunnelAshIcon, ArrowDownAshIcon, ArrowUpAshIcon } from '@glu/icons-react';
import Popover from '@glu/popover-react';
import { AccessibilityText } from '@glu/utilities-react';
import Menu from './Menu/Menu';
import useStyles from './HeaderCell.styles';

const popperOptions = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -4],
      },
    },
  ],
};

const HeaderCell = (props: IHeaderParams) => {
  const { column, displayName, eGridHeader, enableMenu, enableSorting, progressSort } = props;
  const columnDef = column.getColDef();
  const classes = useStyles();
  const [sortDirection, setSortDirection] = useState(column.getSort());
  const [activeFilter, setActiveFilter] = useState(column.isFilterActive());
  const sortIcon = useMemo(() => {
    switch (sortDirection) {
      case 'desc':
        return <ArrowDownAshIcon noIconWrapper />;
      case 'asc':
        return <ArrowUpAshIcon noIconWrapper />;
      default:
        return null;
    }
  }, [sortDirection]);

  const popoverContent = useCallback(
    ({ closePopover }: { closePopover(): void }) => {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <Menu closePopover={closePopover} {...props} />;
    },
    [props]
  );

  useEffect(() => {
    const handleSort = (event: ColumnEvent) => {
      setSortDirection(event.column!.getSort());
    };
    const handleFilter = (event: ColumnEvent) => {
      setActiveFilter(event.column!.isFilterActive());
    };
    const handleWrapperKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (event.ctrlKey) {
          // open up the menu
          const el = event.target as HTMLElement;
          el.querySelector('button')!.click();
        } else if (enableSorting) {
          progressSort();
        }
      }
    };
    const handleWrapperClick = (event: MouseEvent) => {
      // if sorting is not enabled, then don't do anything
      if (!enableSorting) return;
      const el = event.target as HTMLElement;
      // If this is true, you're clicking to open up the column menu
      /* istanbul ignore if */
      if (
        ['BUTTON', 'svg'].includes(el.tagName) ||
        el.classList.contains('ag-header-cell-resize') ||
        el.classList.contains('ag-checkbox-input')
      ) {
        return;
      }
      progressSort();
    };
    eGridHeader.classList.add(classes.wrap);
    eGridHeader.addEventListener('keydown', handleWrapperKeydown);
    eGridHeader.addEventListener('click', handleWrapperClick);
    column.addEventListener('sortChanged', handleSort);
    column.addEventListener('filterChanged', handleFilter);
    return () => {
      eGridHeader.removeEventListener('keydown', handleWrapperKeydown);
      eGridHeader.removeEventListener('click', handleWrapperClick);
      column.removeEventListener('sortChanged', handleSort);
      column.removeEventListener('filterChanged', handleFilter);
    };
  }, [classes.wrap, column, eGridHeader, enableSorting, progressSort]);

  return (
    <div className={classes.root}>
      <p className={classes.displayName}>
        <span>{sortIcon}</span>
        <span className={classes.textWrapper}>{displayName}</span>
      </p>
      {activeFilter && (
        <div className={classes.funnelWrap}>
          <FunnelAshIcon noIconWrapper />
          <AccessibilityText>{locale.get('gridReact.activeFilter')}</AccessibilityText>
        </div>
      )}
      {(columnDef.filter || !columnDef.lockPinned) && enableMenu && (
        <Popover
          className={classes.popover}
          content={popoverContent}
          disableA11yButton
          enablePopoverClick
          noArrow
          placement="bottom-end"
          popperOptions={popperOptions}
        >
          <div>
            <LinesAshIcon
              actionable={{
                'aria-label': locale.get('gridReact.columnMenu'),
                title: locale.get('gridReact.columnMenu'),
                className: classes.menuButton,
              }}
            />
          </div>
        </Popover>
      )}
    </div>
  );
};

export default HeaderCell;
